import React from 'react';
import PropTypes from 'prop-types';
import './style.css';
import {
  FaArrowRight,
  FaCheck,
  FaExclamationTriangle,
  FaTimes,
} from 'react-icons/fa';

const DEFAULT_TARGET_WORD = 'início';
const NOISE_MAX_VOLUME = 38;
const SILENCE_ADVANCE_MS = 3000;
const VOICE_THRESHOLD = 50;
const VOICE_TEST_DURATION_MS = 5000;
const NOISE_CALIBRATION_MS = 1000;
const DB_MIN = -60; // RMS → 0 %
const DB_MAX = -3;  // RMS → 100 %
const NOISE_LEVEL_LOW = 18;
const NOISE_LEVEL_MEDIUM = 40;
const NOISE_LEVEL_HIGH = 62;

class MicrophoneTester extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 1,
      volume: 0,
      noiseLevel: 'silence',

      // step 1
      noiseTestStarted: false,
      noiseCalibrating: false,
      noiseOk: false,
      silenceCountdown: null, // null | 3 | 2 | 1

      // step 2
      voiceTestRunning: false,
      voiceDetected: false,
      voiceTestError: '',

      // step 3
      recognitionStatus: 'idle', // idle | listening | success | error
      transcript: '',
      recognitionError: '',

      hasSpeechAPI: !!(
        window.SpeechRecognition ||
        window.webkitSpeechRecognition ||
        window.msSpeechRecognition
      ),
      micError: '',
    };

    this.audioContext = null;
    this.analyser = null;
    this.stream = null;
    this.animationId = null;
    this.recognition = null;
    this.voiceTestTimer = null;
    this.silenceTimer = null;
    this.silenceCountdownInterval = null;
    this.isUnmounted = false;
    this.noiseBaseline = 0;
    this.calibrationSamples = [];
    this.calibrationStartTime = null;
    this.timeDomainBuffer = null;
  }

  componentDidMount() {
    this.startAudio();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.stopAudio();
    this.stopRecognition();
    if (this.voiceTestTimer) clearTimeout(this.voiceTestTimer);
    this.clearSilenceTimer();
  }

  // ── Audio ──────────────────────────────────────────────────

  getRms = () => {
    if (!this.analyser) return 0;

    const bufferLength = this.analyser.fftSize;
    if (
      !this.timeDomainBuffer ||
      this.timeDomainBuffer.length !== bufferLength
    ) {
      this.timeDomainBuffer = new Uint8Array(bufferLength);
    }

    this.analyser.getByteTimeDomainData(this.timeDomainBuffer);

    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      const sample = (this.timeDomainBuffer[i] - 128) / 128;
      sum += sample * sample;
    }

    return Math.sqrt(sum / bufferLength);
  };

  rmsToVolume = (rms) => {
    if (rms < 0.0001) return 0;
    const db = 20 * Math.log10(rms);
    return Math.max(0, Math.min(100, Math.round(((db - DB_MIN) / (DB_MAX - DB_MIN)) * 100)));
  };

  startAudio = async () => {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      if (this.isUnmounted) {
        this.stopAudio();
        return;
      }

      this.audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;

      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.analyser);

      this.monitorVolume();
    } catch (error) {
      if (this.isUnmounted) return;
      this.setState({ micError: this.getMicError(error) });
    }
  };

  monitorVolume = () => {
    if (!this.analyser || this.isUnmounted) return;

    const update = () => {
      if (!this.analyser || this.isUnmounted) return;

      const rms = this.getRms();
      const rawVolume = this.rmsToVolume(rms);
      let volume = rawVolume;
      let noiseLevel = 'silence';
      let noiseOk = false;

      if (this.state.noiseCalibrating) {
        this.calibrationSamples.push(rms);
        volume = 0;
        noiseLevel = 'silence';
        noiseOk = false;

        if (Date.now() - this.calibrationStartTime >= NOISE_CALIBRATION_MS) {
          this.noiseBaseline =
            this.calibrationSamples.reduce((a, b) => a + b, 0) /
            this.calibrationSamples.length;
          this.calibrationSamples = [];

          if (!this.isUnmounted) {
            this.setState({ noiseCalibrating: false });
          }
        }
      } else {
        // display always uses raw dB volume for visual consistency across steps
        volume = rawVolume;

        if (rawVolume > NOISE_LEVEL_HIGH) noiseLevel = 'high';
        else if (rawVolume > NOISE_LEVEL_MEDIUM) noiseLevel = 'medium';
        else if (rawVolume > NOISE_LEVEL_LOW) noiseLevel = 'low';

        if (this.state.step === 1 && this.state.noiseTestStarted) {
          // noiseOk: current dB must not exceed baseline dB by more than threshold
          const baselineVolume = this.rmsToVolume(this.noiseBaseline);
          noiseOk = rawVolume <= baselineVolume + NOISE_MAX_VOLUME;
        }
      }

      if (!this.isUnmounted) {
        this.setState({ volume, noiseLevel, noiseOk });
      }

      // Step 1: inicia/cancela o timer de avanço automático por silêncio
      if (
        this.state.step === 1 &&
        this.state.noiseTestStarted &&
        !this.state.noiseCalibrating
      ) {
        if (noiseOk) {
          this.startSilenceTimer();
        } else {
          this.clearSilenceTimer();
        }
      }

      if (this.state.voiceTestRunning && !this.state.voiceDetected) {
        if (rawVolume >= VOICE_THRESHOLD) this.onVoiceDetected();
      }

      this.animationId = requestAnimationFrame(update);
    };

    this.animationId = requestAnimationFrame(update);
  };

  stopAudio = () => {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed')
      this.audioContext.close();
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.audioContext = null;
    this.analyser = null;
  };

  getMicError = (error) => {
    if (error.name === 'NotAllowedError')
      return (
        <div style={{ textAlign: 'left' }}>
          <p>
            Permissão de microfone negada.
            <br />
            Para continuar, habilite o acesso ao microfone nas configurações do
            navegador.
          </p>

          <p>
            Clique no ícone ao lado do endereço do site (canto superior
            esquerdo) e permita o uso do microfone.
          </p>

          <p>
            Se precisar de ajuda,{' '}
            <a
              href="https://support.google.com/chrome/answer/2693767?hl=pt-BR"
              target="_blank"
              rel="noreferrer"
            >
              clique aqui
            </a>
            .
          </p>
        </div>
      );
    if (error.name === 'NotFoundError')
      return 'Nenhum microfone encontrado. Conecte um microfone e tente novamente.';
    if (error.name === 'NotReadableError')
      return 'O microfone está sendo usado por outro aplicativo.';
    return 'Não foi possível acessar o microfone.';
  };

  // ── Silence timer (step 1) ─────────────────────────────────

  startSilenceTimer = () => {
    if (this.silenceTimer) return; // já está rodando

    let remaining = Math.round(SILENCE_ADVANCE_MS / 1000);
    this.setState({ silenceCountdown: remaining });

    this.silenceCountdownInterval = setInterval(() => {
      if (this.isUnmounted) return;
      remaining -= 1;
      if (remaining > 0) {
        this.setState({ silenceCountdown: remaining });
      }
    }, 1000);

    this.silenceTimer = setTimeout(() => {
      this.clearSilenceTimer();
      if (!this.isUnmounted) this.goToStep2();
    }, SILENCE_ADVANCE_MS);
  };

  clearSilenceTimer = () => {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.silenceCountdownInterval) {
      clearInterval(this.silenceCountdownInterval);
      this.silenceCountdownInterval = null;
    }
    if (!this.isUnmounted) {
      this.setState({ silenceCountdown: null });
    }
  };

  // ── Step 1 ─────────────────────────────────────────────────

  startNoiseTest = () => {
    this.noiseBaseline = 0;
    this.calibrationSamples = [];
    this.calibrationStartTime = Date.now();
    this.clearSilenceTimer();
    this.setState({
      noiseTestStarted: true,
      noiseCalibrating: true,
      volume: 0,
      noiseLevel: 'silence',
      noiseOk: false,
      silenceCountdown: null,
    });
  };

  goToStep2 = () => {
    this.clearSilenceTimer();
    this.setState({
      step: 2,
      silenceCountdown: null,
      voiceTestRunning: false,
      voiceDetected: false,
      voiceTestError: '',
    });
  };

  // ── Step 2 ─────────────────────────────────────────────────

  startVoiceTest = () => {
    this.setState({
      voiceTestRunning: true,
      voiceDetected: false,
      voiceTestError: '',
    });

    this.voiceTestTimer = setTimeout(() => {
      if (this.isUnmounted || this.state.voiceDetected) return;
      this.setState({
        voiceTestRunning: false,
        voiceTestError:
          'Nenhuma voz detectada. Verifique se o microfone está funcionando e tente novamente.',
      });
    }, VOICE_TEST_DURATION_MS);
  };

  onVoiceDetected = () => {
    if (this.state.voiceDetected) return;
    if (this.voiceTestTimer) {
      clearTimeout(this.voiceTestTimer);
      this.voiceTestTimer = null;
    }
    this.setState({ voiceDetected: true, voiceTestRunning: false });
  };

  goToStep3 = () => {
    if (!this.state.hasSpeechAPI) {
      this.props.onFinish();
      return;
    }
    this.setState({
      step: 3,
      recognitionStatus: 'idle',
      transcript: '',
      recognitionError: '',
    });
  };

  // ── Step 3 ─────────────────────────────────────────────────

  startRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition ||
      window.msSpeechRecognition;
    if (!SpeechRecognition) {
      this.props.onFinish();
      return;
    }

    this.stopRecognition();

    // Suspende o AudioContext para liberar o dispositivo de microfone
    // antes que o SpeechRecognition tente acessá-lo (necessário no Edge/Safari)
    if (this.audioContext && this.audioContext.state === 'running') {
      this.audioContext.suspend().catch(() => {});
    }

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'pt-BR';
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 5;
    this.recognition.continuous = false;

    this.recognition.onstart = () => {
      if (!this.isUnmounted) {
        this.setState({
          recognitionStatus: 'listening',
          transcript: '',
          recognitionError: '',
        });
      }
    };

    this.recognition.onresult = (event) => {
      const alternatives = [];
      for (let i = 0; i < event.results[0].length; i++) {
        alternatives.push(event.results[0][i].transcript);
      }
      const transcript = alternatives.join(' ').split(' ');
      const targetWord = this.props.targetWord || DEFAULT_TARGET_WORD;
      const correct = transcript.some(
        (alt) => this.normalize(alt) === this.normalize(targetWord),
      );

      if (!this.isUnmounted) {
        this.setState({
          recognitionStatus: correct ? 'success' : 'error',
          transcript,
          recognitionError: correct
            ? ''
            : `Palavra não reconhecida. Clique em "Tentar novamente" e diga "${targetWord}".`,
        });
      }
    };

    this.recognition.onerror = (event) => {
      if (this.isUnmounted || event.error === 'aborted') return;
      // Retoma o AudioContext se o reconhecimento falhar
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }
      let msg = 'Erro no reconhecimento de fala. Tente novamente.';
      if (event.error === 'no-speech')
        msg = 'Nenhuma fala detectada. Tente novamente.';
      else if (event.error === 'network')
        msg = 'Erro de rede. Verifique sua conexão.';
      else if (event.error === 'not-allowed')
        msg =
          'Permissão de microfone negada para o reconhecimento de fala. Verifique as permissões do navegador.';
      this.setState({ recognitionStatus: 'error', recognitionError: msg });
    };

    this.recognition.onend = () => {
      // Retoma o AudioContext após o reconhecimento terminar
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }
      if (!this.isUnmounted && this.state.recognitionStatus === 'listening') {
        this.setState({
          recognitionStatus: 'idle',
          recognitionError: 'Nenhuma fala detectada. Tente novamente.',
        });
      }
    };

    this.recognition.start();
  };

  stopRecognition = () => {
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch (e) {}
      this.recognition = null;
    }
  };

  normalize = (str) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toLowerCase()
      .trim();

  // ── Render helpers ─────────────────────────────────────────

  getNoiseLevelLabel = () => {
    const labels = {
      silence: 'Silêncio',
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto',
    };
    return labels[this.state.noiseLevel] || 'Silêncio';
  };

  renderVolumeMeter = () => {
    const { volume, noiseLevel } = this.state;
    return (
      <div className="mic-tester__volume-section">
        <div className="mic-tester__volume-header">
          <span className="mic-tester__volume-label">Nível de áudio</span>
          <span
            className={`mic-tester__volume-badge mic-tester__volume-badge--${noiseLevel}`}
          >
            {this.getNoiseLevelLabel()}
          </span>
        </div>
        <div className="mic-tester__meter-track">
          <div
            className={`mic-tester__meter-fill mic-tester__meter-fill--${noiseLevel}`}
            style={{ width: `${volume}%` }}
          />
        </div>
      </div>
    );
  };

  renderStepIndicator = () => {
    const { step, hasSpeechAPI, recognitionStatus, voiceDetected } = this.state;
    const totalSteps = hasSpeechAPI ? 3 : 2;
    const labels = ['Ruído', 'Voz', 'Reconhecimento'];

    return (
      <div className="mic-tester__steps">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
          <div key={s} className="mic-tester__step-item">
            <div
              className={[
                'mic-tester__step-dot',
                step === s ? 'mic-tester__step-dot--active' : '',
                step > s ||
                (step === 3 && recognitionStatus === 'success') ||
                (step === 2 && voiceDetected && !hasSpeechAPI)
                  ? 'mic-tester__step-dot--done'
                  : '',
              ].join(' ')}
            >
              {step > s ||
              (step === 3 && recognitionStatus === 'success') ||
              (step === 2 && voiceDetected && !hasSpeechAPI) ? (
                <FaCheck />
              ) : (
                s
              )}
            </div>
            <span className="mic-tester__step-label">{labels[s - 1]}</span>
          </div>
        ))}
      </div>
    );
  };

  // ── Steps ──────────────────────────────────────────────────

  renderStep1 = () => {
    const { noiseTestStarted, noiseCalibrating, noiseOk, silenceCountdown } =
      this.state;
    return (
      <div className="mic-tester__step-content">
        <h4 className="mic-tester__step-title">
          Verificação de ruído no ambiente
        </h4>

        {!noiseTestStarted ? (
          <div className="mic-tester__step-body">
            <p className="mic-tester__instruction">
              Certifique-se de que o ambiente esteja silencioso e clique em{' '}
              <strong>"Iniciar teste"</strong>.
            </p>
            <button
              className="mic-tester__btn mic-tester__btn--primary"
              onClick={this.startNoiseTest}
            >
              Iniciar teste
            </button>
          </div>
        ) : (
          <>
            {noiseCalibrating ? (
              <p className="mic-tester__instruction">
                Calibrando o ruído de fundo… Permaneça em silêncio por 1
                segundo.
              </p>
            ) : (
              <p className="mic-tester__instruction">
                Aguarde em silêncio. O teste avançará automaticamente após 3
                segundos sem ruído.
              </p>
            )}
            {!noiseCalibrating && this.renderVolumeMeter()}
            {!noiseCalibrating && (
              <div
                className={`mic-tester__status-msg ${noiseOk ? 'mic-tester__status-msg--ok' : 'mic-tester__status-msg--warn'}`}
              >
                {noiseOk ? (
                  <>
                    <FaCheck /> O ambiente está silencioso.
                    {silenceCountdown !== null && (
                      <span className="mic-tester__silence-countdown">
                        {' '}
                        Avançando em {silenceCountdown}s…
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <FaExclamationTriangle />
                    Muito ruído no ambiente — {this.getNoiseLevelLabel()}.
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  renderStep2 = () => {
    const { voiceTestRunning, voiceDetected, voiceTestError } = this.state;
    return (
      <div className="mic-tester__step-content">
        <h4 className="mic-tester__step-title">Verificação do Microfone</h4>
        {this.renderVolumeMeter()}

        {!voiceTestRunning && !voiceDetected && (
          <div className="mic-tester__step-body">
            <p className="mic-tester__instruction">
              Clique em <strong>“Testar microfone”</strong> e diga qualquer
              palavra no microfone. Depois, clique em <strong>“Próximo”</strong>
              .
            </p>
            {voiceTestError && (
              <div className="mic-tester__error-msg">{voiceTestError}</div>
            )}
            <button
              className="mic-tester__btn mic-tester__btn--primary"
              onClick={this.startVoiceTest}
            >
              Testar microfone
            </button>
          </div>
        )}

        {voiceTestRunning && (
          <div className="mic-tester__listening">
            <div className="mic-tester__pulse-ring" />
            <p className="mic-tester__listening-text">
              Fale agora — diga qualquer palavra...
            </p>
          </div>
        )}

        {voiceDetected && (
          <div className="mic-tester__step-body">
            <div className="mic-tester__status-msg mic-tester__status-msg--ok">
              <FaCheck /> Voz detectada com sucesso.
            </div>
            <button
              className={`mic-tester__btn ${!this.state.hasSpeechAPI ? 'mic-tester__btn--success' : 'mic-tester__btn--primary'}`}
              onClick={this.goToStep3}
            >
              {this.state.hasSpeechAPI ? 'Próximo' : 'Concluir'}{' '}
              <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    );
  };

  renderStep3 = () => {
    const { recognitionStatus, recognitionError } = this.state;
    const targetWord = this.props.targetWord || DEFAULT_TARGET_WORD;

    return (
      <div className="mic-tester__step-content">
        {recognitionStatus === 'idle' && (
          <>
            <h4 className="mic-tester__step-title">Reconhecimento de Fala</h4>
            <p className="mic-tester__instruction">
              Clique em <strong>“Iniciar reconhecimento”</strong> e fale a
              palavra <strong>“abaixo”</strong> no microfone de forma clara e em
              tom natural.
            </p>
            <div className="mic-tester__target-word">{targetWord}</div>
            <div className="mic-tester__step-body">
              {recognitionError && (
                <div className="mic-tester__error-msg">{recognitionError}</div>
              )}
              <button
                className="mic-tester__btn mic-tester__btn--primary"
                onClick={this.startRecognition}
              >
                Iniciar reconhecimento
              </button>
            </div>
          </>
        )}

        {recognitionStatus === 'listening' && (
          <div className="mic-tester__listening">
            <div className="mic-tester__pulse-ring" />
            <p className="mic-tester__listening-text">
              Ouvindo… Fale a palavra <strong>"{targetWord}"</strong> apenas uma
              vez.
            </p>
          </div>
        )}

        {recognitionStatus === 'success' && (
          <div className="mic-tester__step-body">
            <div className="mic-tester__result-icon mic-tester__result-icon--success">
              <FaCheck />
            </div>
            <p className="mic-tester__result-text">
              Teste realizado com sucesso! Clique em <strong>“Concluir”</strong>{' '}
              para finalizar.
            </p>
            <button
              className="mic-tester__btn mic-tester__btn--success"
              onClick={this.props.onFinish}
            >
              Concluir <FaArrowRight />
            </button>
          </div>
        )}

        {recognitionStatus === 'error' && (
          <div className="mic-tester__step-body">
            <div className="mic-tester__result-icon mic-tester__result-icon--error">
              <FaTimes />
            </div>
            <div className="mic-tester__error-msg">{recognitionError}</div>
            <button
              className="mic-tester__btn mic-tester__btn--primary"
              onClick={() => this.setState({ recognitionStatus: 'idle' })}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────

  render() {
    const { step, micError } = this.state;

    return (
      <div className="mic-tester__container">
        <div className="mic-tester">
          <div className="mic-tester__header">
            <h3 className="mic-tester__title">Teste de Microfone</h3>
          </div>

          {micError ? (
            <div className="mic-tester__error-msg mic-tester__error-msg--block">
              {micError}
            </div>
          ) : (
            <>
              {this.renderStepIndicator()}
              {step === 1 && this.renderStep1()}
              {step === 2 && this.renderStep2()}
              {step === 3 && this.renderStep3()}
            </>
          )}
        </div>
      </div>
    );
  }
}

MicrophoneTester.propTypes = {
  targetWord: PropTypes.string,
  onFinish: PropTypes.func.isRequired,
};

MicrophoneTester.defaultProps = {
  targetWord: DEFAULT_TARGET_WORD,
};

export default MicrophoneTester;
